import { h, Component } from 'preact';
import Provider from '@os/context-provider';
import wire from 'wiretie';
import { WeatherModel } from '../../model';
import Location from '@cube/location-widget';
import { HeaderV2 } from '@cube/blocks';
import WeatherDefaultView from './weather';
import WeatherCompactView from './weather-compact';
import { Text } from 'preact-i18n';
import ErrorMessage from '../error-msg';
import WeatherSpinner from '../spinner';
import style from './style';
import cx from 'classnames';
import delve from 'dlv';
import withConfig from '../../config';

export class ContainerDefault extends Component {

	state = {
		currentUnit: this.props.unit || 'F'
	};

	/**
	 * Determine where we need to link off to, and craft the appropriate URL
	 *
	 * @param {Object}  location - Holds data points about a location
	 * @param {string|undefined}  location.country_iso_code
	 * @param {string|undefined}  location.geoname_id
	 * @param {string|Array|undefined}  location.postal_code
	 * @param {boolean} useWeatherChannel - Value set by Gen4 that determines whether we link to the WC or the Portal
	 * @param {Object}  config - Configuration constants from @withConfig
	 * @param {string}  config.weatherAlertWeatherURL - Url to Weather Channels's alert route
	 * @param {string}  config.weatherAlertWeatherPath - Path values needed to properly link off to Weather Channel's alert page
	 * @param {string}  config.weatherAlertNativeURL - Base URL that points to the Portal's weather component/vertical
	 * @return {string|undefined}
	 */
	alertURL = (location, useWeatherChannel, config) => {
		let country = delve(location, 'country_iso_code'),
			geoName = delve(location, 'geoname_id'),
			postalCode,
			url;

		// Handle the value coming in an array or as a string
		if (location.postal_code) {
			if (Array.isArray(location.postal_code)) {
				postalCode = location.postal_code[0];
			}
			else {
				postalCode = location.postal_code;
			}
		}

		// We need both the country and postalCode if we are using the weather channel
		if (useWeatherChannel) {
			if (country && postalCode) {
				url = `${config.weatherAlertWeatherURL}/${postalCode}${config.weatherAlertWeatherPath}${country}`;
			}
		}
		// We can power the portal alert page with just a GeoID value, or
		// both the country and postal code values together.
		else if (geoName || (country && postalCode)) {
			url = `${config.weatherAlertNativeURL}/${geoName || 0}/${country || 0}/${postalCode || 0}`;
		}

		return url;
	};

	/**
	 * Update user's temperature scale preferences locally, and attempt to call
	 * a function named 'setUnits' if it exists on our props object. This props
	 * function currently saves this information to Local Storage and should flow down
	 * back to this component through it's model subscription.
	 *
	 * @param {string|undefined} unit - Temperature unit to set in Local Storage
	 */
	updateTempUnit = unit => {
		if (!unit || typeof unit !== 'string') {
			return;
		}

		if (!this.state.currentUnit || this.state.currentUnit.toUpperCase() !== unit.toUpperCase()){
			this.props.setUnit && this.props.setUnit(unit.toUpperCase());
			this.setState({ currentUnit: unit.toUpperCase() });
		}
	};

	componentWillReceiveProps(nextProps) {
		if (nextProps.unit && this.props.unit !== nextProps.unit) {
			this.setState({ currentUnit: nextProps.unit.toUpperCase() });
		}
	}

	render({
		clientId,
		location,
		locationServiceOrigin,
		notifyCurrentLocationChange,
		conditions,
		alerts,
		forecast,
		unit,
		weatherWidgetConfig: config,
		firstLoad,
		pending,
		rejected,
		localeWeatherUrl, // defined in component_config.es_419.php, a gen4-portal-configuration file
		localeWeatherRadarUrl, // same as above
		compact = false,
		showRadarFullForecastLink,
		showAlerts
	}, { currentUnit }) {
		let content, header;

		if (pending || firstLoad) {
			content = <WeatherSpinner />;
		}
		else if (rejected && rejected.forecast && rejected.conditions || !location || !location.latitude || !location.longitude || !forecast) {
			content = <ErrorMessage />;
		}
		else {
			let weatherAlertUrl = this.alertURL(location, config.useWeatherChannel, config.alertConfig);
			let weatherLink;
			if (typeof localeWeatherUrl !== 'undefined') {
				config.weatherUrl = localeWeatherUrl;
				config.radarFullForecastWeatherURL = localeWeatherUrl;
				weatherLink = localeWeatherUrl;
				config.useWeatherChannel = true;
				weatherAlertUrl = localeWeatherUrl;
			}
			else {
				weatherLink = `${config.weatherLink}/?${config.trackingParams}_${clientId}`;
			}
			content = compact ?
				<WeatherCompactView conditions={conditions} currentUnit={currentUnit} config={config} location={location} changeUnit={this.updateTempUnit} />
				: (
					<WeatherDefaultView conditions={conditions} currentUnit={currentUnit} config={config} forecast={forecast} weatherLink={weatherLink}
						trackingParams={config.trackingParams} clientId={clientId} alerts={alerts} weatherAlertUrl={weatherAlertUrl} changeUnit={this.updateTempUnit}
						showRadarFullForecastLink={showRadarFullForecastLink} showAlerts={showAlerts}
					/>
				);
		}
		header = compact ?
			<Location style="display: none" onChange={notifyCurrentLocationChange} locationServiceOrigin={locationServiceOrigin} />
			: (<HeaderV2 class={style.header} title={(<Text id="weather">Weather</Text>)}>
				<Location
					containerClass={style.location}
					popoverClass={style.popover}
					onChange={notifyCurrentLocationChange}
					anchor="right"
					locationServiceOrigin={locationServiceOrigin}
				/>
			</HeaderV2>);

		return (
			<div class={cx(style.weather)}>
				{ header }
				{ content }
			</div>
		);
	}
}

export const Container = wire(
	'weather',
	({ location = {} }) => location.latitude && location.longitude ? {
		conditions: ['fetchCurrentConditions', location.latitude, location.longitude],
		alerts: ['fetchAlerts', { latitude: location.latitude, longitude: location.longitude }],
		forecast: ['fetchDailyForecast', location.latitude, location.longitude],
		unit: 'getTemperatureScale'
	} : {},
	weather => ({
		setUnit: weather.setTemperatureScale
	})
)( ContainerDefault );

export class WeatherDefault extends Component {
	state = {
		location: {},
		firstLoad: true // @TODO: Can this be fixed in location-widget|model?
	};

	/**
	 * Handle a new location being chosen by the location widget.
	 *
	 * There is a special case here for the first load or first time this
	 * handler is called, as it's often first given an empty object by the
	 * embedded location-widget/model. In this instance we do nothing but
	 * set our 'firstLoad' state to false which will allow any successive
	 * calls to this function with a bad {locationObject} value will be
	 * set into state and the error message handled by the <Container />.
	 *
	 * @param {Object} locationObject
	 * @param {string|Array|undefined} locationObject.postal_code
	 */
	notifyCurrentLocationChange = locationObject => {
		let data = {
			...locationObject,
			postal_code: [].concat(locationObject.postal_code)[0]
		};

		if (!data.postal_code && this.state.firstLoad) {
			this.setState({ firstLoad: false });
		}
		else {
			this.setState({ location: data, firstLoad: false });
		}
	};

	constructor(props, context) {
		super(props, context);

		let locationServiceOrigin = delve(props, 'locationServiceOrigin'),
			weatherServiceOrigin = delve(props, 'weatherServiceOrigin'),
			config = {};

		if (locationServiceOrigin) {
			config.locationServiceOrigin = locationServiceOrigin;
		}

		if (weatherServiceOrigin) {
			config.weatherServiceOrigin = weatherServiceOrigin;
		}

		this.weatherProvider = new WeatherModel(config);
	}

	render({ locationServiceOrigin, weatherWidgetConfig, ...props }, { location, firstLoad }) {
		return (
			<div>
				<Provider weather={this.weatherProvider}>
					<Container
						{...props}
						notifyCurrentLocationChange={this.notifyCurrentLocationChange}
						locationServiceOrigin={locationServiceOrigin}
						weatherWidgetConfig={weatherWidgetConfig}
						location={location}
						firstLoad={firstLoad}
					/>
				</Provider>
			</div>
		);
	}
}

export default withConfig([
	'locationServiceOrigin',
	'weatherServiceOrigin',
	'weatherWidgetConfig',
	'clientId',
	'showAlerts'
])(WeatherDefault);



// WEBPACK FOOTER //
// ../node_modules/@cube/weather-widget/src/components/weather/index.js